import React, { useCallback, useRef, useState } from "react";

const PAIRS = [
  {
    id: "colonial",
    label: "Colonial · Monmouth County",
    before:
      "https://static.prod-images.emergentagent.com/jobs/50fbfbe1-e930-4cc7-8453-ea3806374a6a/images/220c1851b0c38f209fcf4ad39601c55fc075a5223445699faa748a38a5e800da.jpeg",
    after:
      "https://static.prod-images.emergentagent.com/jobs/50fbfbe1-e930-4cc7-8453-ea3806374a6a/images/947d27118b72db136b6e3f5e1872307d47c7970f20b49020c4300be2c88d8880.jpeg",
  },
  {
    id: "capecod",
    label: "Cape Cod · Jersey Shore",
    before:
      "https://static.prod-images.emergentagent.com/jobs/50fbfbe1-e930-4cc7-8453-ea3806374a6a/images/6a32cc6efdb932fd0f830a65ae019f9f8f4149d1be0f0f1e3d7638a00c30e543.jpeg",
    after:
      "https://static.prod-images.emergentagent.com/jobs/50fbfbe1-e930-4cc7-8453-ea3806374a6a/images/fd0f27bd39358a440fda6a7bff5ea4fb7ee5f6a6f4416a6fc0f4700932f918bf.jpeg",
  },
  {
    id: "ranch",
    label: "Brick Ranch · Central NJ",
    before:
      "https://static.prod-images.emergentagent.com/jobs/50fbfbe1-e930-4cc7-8453-ea3806374a6a/images/1774ed933ebadb17083798339b8bd9e3963874387c7d3117ac9935e6421fde3e.jpeg",
    after:
      "https://static.prod-images.emergentagent.com/jobs/50fbfbe1-e930-4cc7-8453-ea3806374a6a/images/b7d2c88e94a413877ebecb4c9edbbee4805a5ac4d172b9912208b29812990db6.jpeg",
  },
];

export default function BeforeAfterGallery() {
  const [active, setActive] = useState(0);
  const pair = PAIRS[active];

  return (
    <div className="mt-20 md:mt-28" data-testid="before-after-gallery">
      <div className="grid md:grid-cols-12 gap-8 mb-10 items-end">
        <div className="md:col-span-6 reveal">
          <div className="flex items-center gap-4 mb-5">
            <span className="hairline w-10" />
            <span className="eyebrow">Before &amp; After</span>
          </div>
          <h3 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)]">
            The same roof, <em className="text-[var(--accent)]">years younger.</em>
          </h3>
        </div>
        <div className="md:col-span-5 md:col-start-8 reveal reveal-delay-1">
          <p className="text-[var(--text-secondary)] leading-relaxed">
            Drag the divider to compare a faded, dried-out roof with the same
            roof after a single rejuvenation treatment.
          </p>
        </div>
      </div>

      <CompareSlider key={pair.id} before={pair.before} after={pair.after} />

      <div
        className="mt-6 flex flex-wrap gap-3"
        data-testid="before-after-thumbnails"
      >
        {PAIRS.map((p, i) => (
          <button
            key={p.id}
            onClick={() => setActive(i)}
            data-testid={`before-after-thumb-${p.id}`}
            className={`group relative overflow-hidden border transition-colors duration-300 ${
              i === active
                ? "border-[var(--accent)]"
                : "border-white/15 hover:border-white/40"
            }`}
          >
            <img
              src={p.after}
              alt={p.label}
              className="h-20 w-32 md:h-24 md:w-40 object-cover"
              loading="lazy"
            />
            <span
              className={`absolute inset-x-0 bottom-0 px-2 py-1 font-body text-[9px] uppercase tracking-[0.15em] text-left backdrop-blur-sm transition-colors ${
                i === active
                  ? "bg-black/70 text-[var(--accent)]"
                  : "bg-black/60 text-[var(--text-secondary)]"
              }`}
            >
              {p.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function CompareSlider({ before, after }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    ref.current.setPointerCapture?.(e.pointerId);
    update(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging.current) update(e.clientX);
  };
  const onPointerUp = () => {
    dragging.current = false;
  };

  return (
    <div
      ref={ref}
      className="relative w-full h-[340px] md:h-[560px] overflow-hidden border border-white/10 select-none cursor-ew-resize touch-none"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      data-testid="before-after-slider"
    >
      {/* After (base layer) */}
      <img
        src={after}
        alt="Roof after rejuvenation treatment"
        className="absolute inset-0 w-full h-full object-cover"
        draggable="false"
      />
      {/* Before (clipped layer) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={before}
          alt="Faded roof before rejuvenation"
          className="absolute inset-0 w-full h-full object-cover"
          draggable="false"
        />
      </div>

      {/* Divider + handle */}
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-[var(--accent)]"
        style={{ left: `${pos}%` }}
      >
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-12 w-12 rounded-full border border-[var(--accent)] bg-black/70 backdrop-blur-md flex items-center justify-center text-[var(--accent)]"
          data-testid="before-after-handle"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M8 7l-5 5 5 5M16 7l5 5-5 5" />
          </svg>
        </div>
      </div>

      {/* Labels */}
      <span className="absolute top-4 left-4 px-3 py-1.5 font-body text-[10px] uppercase tracking-[0.25em] bg-black/60 backdrop-blur-sm text-[var(--text-secondary)] border border-white/10 pointer-events-none">
        Before
      </span>
      <span className="absolute top-4 right-4 px-3 py-1.5 font-body text-[10px] uppercase tracking-[0.25em] bg-black/60 backdrop-blur-sm text-[var(--accent)] border border-[var(--accent)]/40 pointer-events-none">
        After
      </span>
    </div>
  );
}
